<template>
    <SpinnerBlock v-if="isSearching" />
    <div v-else class="client-info">
        <!-- Header with Client Identity and Action Buttons -->
        <div class="info-header">
            <div class="client-identity">
                <div class="avatar" :style="{ backgroundColor: client.bIsactive_client ? '#0A5C9E' : '#6B7280' }">
                    {{ client.initials }}
                </div>
                <div class="identity-details">
                    <h2>{{ client.fullname_client }}</h2>
                    <div class="identity-meta">
                        <span class="username">{{ client.username_client }}</span>
                        <span class="client-id">{{ client.userpk_client }}</span>
                    </div>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons">
                <!-- Payment Button - Always visible -->
                <button class="btn btn-payment" @click="handlePayment">
                    <span class="btn-icon">💰</span>
                    Accept Payment
                </button>

                <!-- Adjust Bill Button -->
                <button class="btn btn-bill" @click="handleAdjustBill">
                    <span class="btn-icon">📝</span>
                    Adjust Bill
                </button>

                <!-- Edit Client Button -->
                <button class="btn btn-edit" @click="handleEditClient">
                    <span class="btn-icon">✏️</span>
                    Edit
                </button>

                <!-- Toggle Status Button - Changes based on active status -->
                <button class="btn" :class="client.bIsactive_client ? 'btn-deactivate' : 'btn-activate'"
                    @click="handleToggleStatus">
                    <span class="btn-icon">{{ client.bIsactive_client ? '🔴' : '🟢' }}</span>
                    {{ client.bIsactive_client ? 'Deactivate' : 'Activate' }}
                </button>

                <!-- More Actions Dropdown (Optional) -->
                <div class="dropdown" ref="dropdownRef" :class="{ 'show': showDropdown }">
                    <button class="btn btn-more" @click.stop="toggleDropdown" type="button" data-toggle="dropdown"
                        aria-haspopup="true" :aria-expanded="showDropdown">
                        <span class="btn-icon">⋯</span>
                    </button>
                    <div class="dropdown-menu" :class="{ 'show': showDropdown }" v-if="showDropdown" @click.stop>
                        <button class="dropdown-item" type="button" @click="handleViewHistory">
                            <span>📋</span> View Full History
                        </button>
                        <button class="dropdown-item" type="button" @click="handlePrintBill">
                            <span>🖨️</span> Print Bill
                        </button>
                        <button class="dropdown-item" type="button" @click="handleSendNotification">
                            <span>📱</span> Send Notification
                        </button>
                        <button class="dropdown-item" type="button" @click="handleExportData">
                            <span>📊</span> Export Data
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Status Tags (Moved below header for better spacing) -->
        <div class="status-tags">
            <span class="tag"
                :class="{ 'tag-active': client.bIsactive_client, 'tag-inactive': !client.bIsactive_client }">
                {{ client.bIsactive_client ? 'ACTIVE' : 'INACTIVE' }}
            </span>
            <span class="tag" :class="{ 'tag-online': client.isOnline, 'tag-offline': !client.isOnline }">
                {{ client.isOnline ? 'ONLINE' : 'OFFLINE' }}
            </span>
            <span class="tag"
                :class="{ 'tag-billed': client.bIsbilled_client, 'tag-unbilled': !client.bIsbilled_client }">
                {{ client.bIsbilled_client ? 'BILLED' : 'UNBILLED' }}
            </span>
        </div>

        <!-- Rest of your existing template remains exactly the same -->
        <!-- Quick Stats -->
        <div class="quick-stats">
            <div class="stat-item">
                <span class="stat-label">Monthly Due</span>
                <span class="stat-value">{{ Formatters.formatCurrency(client.monthlydue_client) }}</span>
            </div>
            <div class="stat-item">
                <span class="stat-label">Balance</span>
                <span class="stat-value"
                    :class="{ 'text-positive': client.balance_client > 0, 'text-negative': client.balance_client < 0 }">
                    {{ Formatters.formatCurrency(client.balance_client) }}
                </span>
            </div>
            <div class="stat-item">
                <span class="stat-label">Profile</span>
                <span class="stat-value">{{ client.profile_client }}</span>
            </div>
            <div class="stat-item">
                <span class="stat-label">Due Day</span>
                <span class="stat-value">{{ client.due_client }}</span>
            </div>
        </div>

        <!-- Main Information Grid -->
        <div class="info-grid">
            <!-- Personal & Account Details -->
            <div class="info-card">
                <h3>
                    <span class="card-icon">👤</span>
                    Account Details
                </h3>
                <div class="info-rows">
                    <div class="info-row">
                        <span class="row-label">Date Added</span>
                        <span class="row-value">{{ Formatters.formatDateTime(client.dateadded_client) }}</span>
                    </div>
                    <div class="info-row">
                        <span class="row-label">Date Installed</span>
                        <span class="row-value">{{ client.dateinstalled_client ?
                            Formatters.formatDateTime(client.dateinstalled_client) : '—' }}</span>
                    </div>
                    <div class="info-row">
                        <span class="row-label">Last Logout</span>
                        <span class="row-value">{{ Formatters.formatDateTime(client.lastlogoutdate_client) }}</span>
                    </div>
                    <div class="info-row">
                        <span class="row-label">Auto-disconnect</span>
                        <span class="row-value">
                            <span class="boolean-indicator" :class="{ 'true': client.bIsautodisconnect_client }">
                                {{ client.bIsautodisconnect_client ? 'Enabled' : 'Disabled' }}
                            </span>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Contact & Location -->
            <div class="info-card">
                <h3>
                    <span class="card-icon">📍</span>
                    Contact & Location
                </h3>
                <div class="info-rows">
                    <div class="info-row">
                        <span class="row-label">Mobile Number</span>
                        <span class="row-value">{{ client.mobileno_client || '—' }}</span>
                    </div>
                    <div class="info-row">
                        <span class="row-label">Barangay</span>
                        <span class="row-value">{{ client.brgy_client || '—' }}</span>
                    </div>
                    <div class="info-row">
                        <span class="row-label">Sitio</span>
                        <span class="row-value">{{ client.sitio_client || '—' }}</span>
                    </div>
                    <div class="info-row">
                        <span class="row-label">Coordinates</span>
                        <span class="row-value" v-if="client.lat_client && client.lng_client">
                            {{ client.lat_client }}, {{ client.lng_client }}
                        </span>
                        <span class="row-value" v-else>—</span>
                    </div>
                </div>
            </div>

            <!-- Network Details -->
            <div class="info-card">
                <h3>
                    <span class="card-icon">🌐</span>
                    Network Details
                </h3>
                <div class="info-rows">
                    <div class="info-row">
                        <span class="row-label">NAP</span>
                        <span class="row-value">{{ client.nap_client || '—' }}</span>
                    </div>
                    <div class="info-row">
                        <span class="row-label">Port</span>
                        <span class="row-value">{{ client.port_client || '—' }}</span>
                    </div>
                    <div class="info-row">
                        <span class="row-label">Description</span>
                        <span class="row-value">{{ client.description_client || '—' }}</span>
                    </div>
                    <div class="info-row">
                        <span class="row-label">Remarks</span>
                        <span class="row-value">{{ client.remarks_client || '—' }}</span>
                    </div>
                </div>
            </div>

            <!-- Activity Summary -->
            <div class="info-card">
                <h3>
                    <span class="card-icon">📊</span>
                    Activity Summary
                </h3>
                <div class="info-rows">
                    <div class="info-row">
                        <span class="row-label">Total Sessions</span>
                        <span class="row-value">{{ currentInfo.activityLogs?.length }}</span>
                    </div>
                    <div class="info-row">
                        <span class="row-label">Total Offline Time</span>
                        <span class="row-value">{{ Formatters.formatTotalTime(totalMinsOnline) }}</span>
                    </div>
                    <div class="info-row">
                        <span class="row-label">Last Session</span>
                        <span class="row-value">{{ lastActiveDate }}</span>
                    </div>
                    <div class="info-row">
                        <span class="row-label">Recovery Marker</span>
                        <span class="row-value">{{ client.recMarker || '—' }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Financial Summary -->
        <div class="financial-section">
            <h3>
                <span class="section-icon">💰</span>
                Financial Overview
            </h3>
            <div class="financial-grid">
                <div class="financial-item">
                    <span class="financial-label">Total Payments</span>
                    <span class="financial-value positive">{{ Formatters.formatCurrency(totalPayments) }}</span>
                </div>
                <div class="financial-item">
                    <span class="financial-label">Total Bills</span>
                    <span class="financial-value negative">{{ Formatters.formatCurrency(totalBills) }}</span>
                </div>
                <div class="financial-item">
                    <span class="financial-label">Net Balance</span>
                    <span class="financial-value" :class="{ 'positive': netBalance > 0, 'negative': netBalance < 0 }">
                        {{ Formatters.formatCurrency(netBalance) }}
                    </span>
                </div>
                <div class="financial-item">
                    <span class="financial-label">Current Balance</span>
                    <span class="financial-value"
                        :class="{ 'positive': client.balance_client > 0, 'negative': client.balance_client < 0 }">
                        {{ Formatters.formatCurrency(client.balance_client) }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Recent Transactions -->
        <div class="transactions-section" v-if="currentInfo.transactions?.length > 0">
            <h3>
                <span class="section-icon">💳</span>
                Recent Transactions
            </h3>
            <div class="transactions-list">
                <div v-for="trans in currentInfo.transactions" :key="trans.syspk_te" class="transaction-entry">
                    <div class="transaction-date">{{ Formatters.formatDate(trans.dateissued_te) }}</div>
                    <div class="transaction-type">
                        <span class="type-badge" :class="trans.module_te.toLowerCase()">
                            {{ trans.module_te }}
                        </span>
                    </div>
                    <div class="transaction-ref"><span v-if="trans.module_te !== 'BILLING'">{{ trans.transref_te !==
                        '000000' ? trans.transref_te : trans.syspk_te.toString().padStart(8, '0')
                            }}</span>
                        <span v-if="trans.module_te === 'BILLING'">{{ trans.syspk_te.toString().padStart(8, '0')
                        }}</span>
                        <br />
                        <span v-if="trans.module_te !== 'BILLING'">{{ Formatters.formatDateTime(trans.dateissued_te)
                        }}</span>
                        <span v-else>{{ Formatters.formatDateTime(trans.datedue_te) }}</span>
                    </div>
                    <div class="transaction-amount d-flex align-items-center justify-content-end gap-2"
                        :class="{ 'positive': trans.amount_te < 0, 'negative': trans.amount_te > 0 }">
                        <span class="fw-semibold">
                            {{ Formatters.formatCurrency(Math.abs(trans.amount_te)) }}
                        </span>
                        <button v-if="trans.module_te !== 'PAYMENT' && hasRoles(['can-delete'])"
                            @click="deleteBill(trans.syspk_te)"
                            class="btn btn-sm btn-outline-danger border-0 rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
                            style="width: 28px; height: 28px;" title="Delete transaction">
                            <i class="bi bi-trash fs-6"></i>
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="currentInfo.transactions.length > 5" class="view-more">
                <span>+{{ currentInfo.transactions.length - 5 }} more transactions</span>
            </div>
        </div>

        <!-- Recent Activity Logs -->
        <div class="logs-section" v-if="currentInfo.activityLogs?.length > 0">
            <h3>
                <span class="section-icon">📋</span>
                Recent Activity Logs
            </h3>
            <div class="logs-list">
                <div v-for="log in currentInfo.activityLogs" :key="log.syspk_acclog" class="log-entry">
                    <div class="log-time">{{ Formatters.formatRelativeTime(log.dateadded_acclog) }}</div>
                    <div class="log-duration">{{ Formatters.formatMinutes(log.totalmins_acclog) }}</div>
                    <div class="log-status" :class="{ 'claimed': log.claimed === 1 }">
                        {{ log.claimed === 1 ? 'Claimed' : 'Unclaimed' }}
                    </div>
                </div>
            </div>
            <div v-if="currentInfo.activityLogs?.length > 5" class="view-more">
                <span>+{{ currentInfo.activityLogs?.length - 5 }} more sessions</span>
            </div>
        </div>



        <!-- Audit Logs -->
        <div class="audit-section" v-if="currentInfo.userActionLogs.length > 0">
            <h3>
                <span class="section-icon">🔍</span>
                Audit Trail
            </h3>
            <div class="audit-list">
                <div v-for="log in currentInfo.userActionLogs" :key="log.syspk_ual" class="audit-entry">
                    <div class="audit-header">
                        <span class="audit-action">{{ log.type_ual }}</span>
                        <span class="audit-user">by {{ log.fullname_user }}</span>
                    </div>
                    <div class="audit-details">{{ log.payload_ual }}</div>
                    <div class="audit-time">{{ Formatters.formatRelativeTime(log.addeddate_ual) }}</div>
                </div>
            </div>
        </div>
    </div>
    <ChildModal title="Bill Adjustment" :show="showAdjustmentModal" @close="toggleAdjustmentModal">
        <BillAdjustmentForm @cancel="toggleAdjustmentModal" @adjustment-applied="handleAdjustmentApplied"
            ref="billAdjFormRef" />
    </ChildModal>
    <ChildModal :closeable="false" title="Accept Payment" :show="showAcceptPaymentModal"
        @close="toggleAcceptPaymentModal">
        <AcceptPaymentForm @cancel="toggleAcceptPaymentModal" @payment-accepted="handlePaymentAccepted"
            ref="acceptPaymentRef" />
    </ChildModal>
    <ChildModal :show="showBluetoothPrinterModal" @close="toggleBluetoothPrinterModal" title="Test Bluetooth Print">
        <BluetoothPrinter />
    </ChildModal>
    <FullscreenModal2 :show="showBillModal" @close="toggleBillModal">
        <ClientBill :client="client" :bills="currentInfo.transactions" />
    </FullscreenModal2>
    <FullscreenModal2 :show="showEditClientModal" @close="toggleEditClientModal">
        <EditClientInfo @cancel="handleCancelEdit" @update-success="handleUpdateSuccess" ref="editClientRef" />
    </FullscreenModal2>
</template>

<script setup>
import clientService from '@/services/clientService';
import { computed, ref, onMounted, onUnmounted } from 'vue';
import SpinnerBlock from '../animations/SpinnerBlock.vue';
import Formatters from '@/utils/formatters';
import ChildModal from '../modals/ChildModal.vue';
import BillAdjustmentForm from '../inputs/BillAdjustmentForm.vue';
import confirmationService from '@/services/confirmationService';
import { hasRoles } from '@/utils/auth';
import transactionService from '@/services/transactionService';
import toastService from '@/services/toastService';
import AcceptPaymentForm from '../inputs/AcceptPaymentForm.vue';
import BluetoothPrinter from '../utils/BluetoothPrinter.vue';
import FullscreenModal2 from '../modals/FullscreenModal2.vue';
import ClientBill from './ClientBill.vue';
import EditClientInfo from './EditClientInfo.vue';
const showEditClientModal = ref(false);
const editClientRef = ref(null);
const currentInfo = ref({ transactions: [], userActionLogs: [], activityLogs: [], client: {} });
const isSearching = ref(false);
const client = ref({});
const showDropdown = ref(false);
const dropdownRef = ref(null);
const billAdjFormRef = ref(null);
const showAdjustmentModal = ref(false);
const acceptPaymentRef = ref(null);
const showAcceptPaymentModal = ref(false);
const showBluetoothPrinterModal = ref(false);
const toggleAdjustmentModal = () => {
    showAdjustmentModal.value = !showAdjustmentModal.value;
}
const toggleAcceptPaymentModal = () => {
    showAcceptPaymentModal.value = !showAcceptPaymentModal.value;
}

const getClientInfo = async (id) => {
    isSearching.value = true;
    currentInfo.value = await clientService.getClientInfo(id);
    client.value = currentInfo.value.client;
    isSearching.value = false;
}

const totalPayments = computed(() => {
    if (!currentInfo.value.transactions) return 0;

    return currentInfo.value.transactions
        .filter(t => t.module_te === 'PAYMENT')
        .reduce((sum, t) => sum + Math.abs(t.amount_te), 0);
});

const toggleEditClientModal = ()=>{
    showEditClientModal.value = !showEditClientModal.value;
}

const handlePaymentAccepted = (client) => {
    toggleAcceptPaymentModal();
    getClientInfo(client.syspk_client);
}
const toggleBluetoothPrinterModal = () => {
    showBluetoothPrinterModal.value = !showBluetoothPrinterModal.value;
}

const handleCancelEdit = ()=>{
    toggleEditClientModal();
}

const handleUpdateSuccess = (client)=>{
    toggleEditClientModal();
    getClientInfo(client.syspk_client);
}

const handleEditClient = () => {
    toggleEditClientModal();
    editClientRef.value.setClient(client.value);
}





const deleteBill = async (id) => {
    const confirmed = await confirmationService.show("This action cannot be undone. Are you sure you want to delete this item?");
    if (!confirmed) return;
    const resp = await transactionService.deleteBill(id);
    if (resp.code > -1) {
        toastService.success("Item removed successfully!");
        getClientInfo(client.value.syspk_client);
    }
    else {
        toastService.error(resp.message);
    }
}

const totalBills = computed(() => {
    if (!currentInfo.value.transactions) return 0;

    return currentInfo.value.transactions
        .filter(t => t.module_te === 'BILLING')
        .reduce((sum, t) => sum + t.amount_te, 0);
});

const netBalance = computed(() => {
    return totalPayments.value - totalBills.value;
});

const totalMinsOnline = computed(() => {
    if (!currentInfo.value.activityLogs) return 0;
    return currentInfo.value.activityLogs.reduce((sum, log) => sum + (log.totalmins_acclog || 0), 0);
});

const lastActiveDate = computed(() => {
    if (!currentInfo.value.activityLogs?.length) return 'Never';

    const validLogs = currentInfo.value.activityLogs
        .filter(log => log.totalmins_acclog > 0)
        .sort((a, b) => new Date(b.dateadded_acclog) - new Date(a.dateadded_acclog));

    return validLogs.length > 0 ? Formatters.formatRelativeTime(validLogs[0].dateadded_acclog) : 'Never';
});

// Action Handlers
const handlePayment = () => {
    // Emit event or trigger payment modal
    //emit('accept-payment', client.value);
    toggleAcceptPaymentModal();
    acceptPaymentRef.value.setClient(client.value);
};

const handleAdjustBill = () => {
    //emit('adjust-bill', client.value);
    toggleAdjustmentModal();
    billAdjFormRef.value.setClient(client.value);
};

const handleAdjustmentApplied = (client) => {
    toggleAdjustmentModal();
    getClientInfo(client.syspk_client);
}


const handleToggleStatus = async () => {
    const action = client.value.isactive_client === 1 ? 'deactivate' : 'activate';
    const confirmed = await confirmationService.show(`Are you sure you want to ${action} ${client.value.fullname_client}?`);
    if (!confirmed)
        return;
    if (action === 'activate') {
        const resp = await clientService.activate(client.value.syspk_client);
        if (resp.code > -1) {
            toastService.success("Client activated!");
            getClientInfo(client.value.syspk_client);
        }
        else {
            toastService.error(resp.message);
        }
    }
    else {
        const resp = await clientService.deactivate(client.value.syspk_client);
        if (resp.code > -1) {
            toastService.success("Client activated!");
            getClientInfo(client.value.syspk_client);
        }
        else {
            toastService.error(resp.message);
        }
    }
};

const handleViewHistory = () => {
    emit('view-history', client.value);
    showDropdown.value = false;
};
const showBillModal = ref(false);
const toggleBillModal = () => {
    showBillModal.value = !showBillModal.value;
}
const handlePrintBill = () => {
    toggleBillModal();
    //emit('print-bill', client.value);
    showDropdown.value = false;
};

const handleSendNotification = () => {
    emit('send-notification', client.value);
    showDropdown.value = false;
};

const handleExportData = () => {
    toggleBluetoothPrinterModal();
    // emit('export-data', client.value);
    showDropdown.value = false;
};

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
    if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
        showDropdown.value = false;
    }
};

// Handle escape key
const handleEscKey = (event) => {
    if (event.key === 'Escape' && showDropdown.value) {
        showDropdown.value = false;
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    // Prevent Bootstrap from interfering
    if (dropdownRef.value) {
        dropdownRef.value.addEventListener('hide.bs.dropdown', (e) => {
            e.preventDefault();
            e.stopPropagation();
        });
    }
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    document.removeEventListener('keydown', handleEscKey);
});

const toggleDropdown = () => {
    showDropdown.value = !showDropdown.value;
};
// eslint-disable-next-line 
defineExpose({ getClientInfo });
// eslint-disable-next-line 
const emit = defineEmits([
    'accept-payment',
    'adjust-bill',
    'toggle-status',
    'view-history',
    'print-bill',
    'send-notification',
    'export-data'
]);
</script>

<style scoped>
/* Add these new styles for action buttons */
.action-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
}

.btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.btn-icon {
    font-size: 16px;
}

.btn-payment {
    background: #059669;
    color: white;
}

.btn-payment:hover {
    background: #047857;
}

.btn-bill {
    background: #D97706;
    color: white;
}

.btn-bill:hover {
    background: #B45309;
}

.btn-edit {
    background: #3B82F6;
    color: white;
}

.btn-edit:hover {
    background: #2563EB;
}

.btn-activate {
    background: #059669;
    color: white;
}

.btn-activate:hover {
    background: #047857;
}

.btn-deactivate {
    background: #DC2626;
    color: white;
}

.btn-deactivate:hover {
    background: #B91C1C;
}

.btn-more {
    background: #6B7280;
    color: white;
    padding: 8px 12px;
}

.btn-more:hover {
    background: #4B5563;
}

/* Dropdown Styles */
.dropdown {
    position: relative;
    display: inline-block;
}

.dropdown-menu {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 200px;
    z-index: 1000;
    border: 1px solid #E5E7EB;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 16px;
    border: none;
    background: none;
    font-size: 13px;
    color: #374151;
    cursor: pointer;
    transition: background 0.2s;
    text-align: left;
}

.dropdown-item:first-child {
    border-radius: 8px 8px 0 0;
}

.dropdown-item:last-child {
    border-radius: 0 0 8px 8px;
}

.dropdown-item:hover {
    background: #F3F4F6;
}

/* Update existing styles */
.info-header {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid #E5E7EB;
}

@media (min-width: 768px) {
    .info-header {
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
    }
}

.status-tags {
    margin-bottom: 20px;
}

/* Rest of your existing styles remain exactly the same */
.client-info {
    max-width: 800px;
    margin: 0 auto;
}

.client-identity {
    display: flex;
    align-items: center;
    gap: 16px;
}

.avatar {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: 600;
    color: white;
    text-transform: uppercase;
    background-color: #0A5C9E;
}

.identity-details h2 {
    font-size: 20px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 4px 0;
}

.identity-meta {
    display: flex;
    gap: 12px;
    font-size: 13px;
}

.username {
    color: #0A5C9E;
    font-weight: 500;
}

.client-id {
    color: #6B7280;
}

.status-tags {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.tag {
    padding: 4px 10px;
    border-radius: 16px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
}

.tag-active {
    background: #DEF7EC;
    color: #03543F;
}

.tag-inactive {
    background: #FDE2E2;
    color: #9B1C1C;
}

.tag-online {
    background: #DEF7EC;
    color: #03543F;
}

.tag-offline {
    background: #F3F4F6;
    color: #4B5563;
}

.tag-billed {
    background: #E1EFFE;
    color: #1E429F;
}

.tag-unbilled {
    background: #FEF3C7;
    color: #92400E;
}

.quick-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;
    padding: 16px;
    background: #F9FAFB;
    border-radius: 12px;
}

.stat-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.stat-label {
    font-size: 11px;
    color: #6B7280;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.stat-value {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 24px;
}

.info-card {
    background: #F9FAFB;
    border-radius: 12px;
    padding: 16px;
}

.info-card h3 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 16px 0;
    padding-bottom: 12px;
    border-bottom: 1px solid #E5E7EB;
}

.card-icon {
    font-size: 18px;
}

.info-rows {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 14px;
}

.row-label {
    color: #6B7280;
    font-weight: 500;
}

.row-value {
    color: #111827;
    font-weight: 500;
    text-align: right;
}

.boolean-indicator {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 12px;
    background: #F3F4F6;
    color: #4B5563;
    font-size: 12px;
}

.boolean-indicator.true {
    background: #DEF7EC;
    color: #03543F;
}

.financial-section {
    margin-bottom: 24px;
    padding: 20px;
    background: linear-gradient(to right, #F9FAFB, #FFFFFF);
    border-radius: 12px;
    border: 1px solid #E5E7EB;
}

.financial-section h3 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 16px 0;
}

.section-icon {
    font-size: 18px;
}

.financial-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
}

.financial-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.financial-label {
    font-size: 11px;
    color: #6B7280;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.financial-value {
    font-size: 18px;
    font-weight: 600;
}

.positive {
    color: #059669;
}

.negative {
    color: #DC2626;
}

.logs-section,
.transactions-section,
.audit-section {
    margin-bottom: 20px;
    padding: 16px;
    background: #FFFFFF;
    border: 1px solid #E5E7EB;
    border-radius: 12px;
}

.logs-section h3,
.transactions-section h3,
.audit-section h3 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 16px 0;
}

.logs-list,
.transactions-list,
.audit-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.log-entry {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    background: #F9FAFB;
    border-radius: 8px;
    font-size: 13px;
}

.log-time {
    color: #6B7280;
}

.log-duration {
    font-weight: 600;
    color: #0A5C9E;
}

.log-status {
    padding: 2px 8px;
    border-radius: 12px;
    background: #F3F4F6;
    color: #6B7280;
    font-size: 11px;
    font-weight: 500;
}

.log-status.claimed {
    background: #DEF7EC;
    color: #03543F;
}

.transaction-entry {
    display: grid;
    grid-template-columns: auto 80px 1fr auto;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background: #F9FAFB;
    border-radius: 8px;
    font-size: 13px;
}

.transaction-date {
    color: #6B7280;
}

.type-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
}

.type-badge.payment {
    background: #DEF7EC;
    color: #03543F;
}

.type-badge.billing {
    background: #FEE2E2;
    color: #991B1B;
}

.transaction-ref {
    color: #4B5563;
    font-family: monospace;
}

.transaction-amount {
    font-weight: 600;
    text-align: right;
}

.audit-entry {
    padding: 12px;
    background: #F9FAFB;
    border-radius: 8px;
    border-left: 3px solid #0A5C9E;
}

.audit-header {
    display: flex;
    gap: 8px;
    margin-bottom: 6px;
}

.audit-action {
    font-weight: 600;
    color: #0A5C9E;
}

.audit-user {
    color: #6B7280;
    font-size: 12px;
}

.audit-details {
    font-size: 13px;
    color: #374151;
    margin-bottom: 6px;
    white-space: pre-wrap;
    word-break: break-word;
}

.audit-time {
    font-size: 11px;
    color: #9CA3AF;
}

.view-more {
    margin-top: 12px;
    text-align: center;
    font-size: 12px;
    color: #0A5C9E;
    cursor: pointer;
    padding: 6px;
    border-top: 1px dashed #E5E7EB;
}

.text-positive {
    color: #059669;
}

.text-negative {
    color: #DC2626;
}

@media (max-width: 640px) {
    .quick-stats {
        grid-template-columns: repeat(2, 1fr);
    }

    .info-grid {
        grid-template-columns: 1fr;
    }

    .financial-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .transaction-entry {
        grid-template-columns: 1fr;
        gap: 4px;
    }

    .action-buttons {
        width: 100%;
        justify-content: flex-start;
    }
}
</style>